import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { PlusIcon, ChatBubbleLeftEllipsisIcon, UserCircleIcon, Bars3Icon, XMarkIcon, QuestionMarkCircleIcon, SunIcon, MoonIcon } from '@heroicons/react/24/outline';
import { ReactComponent as BrandIcon } from '../assets/BrandIcon.svg';
import FeedbackModal from './FeedbackModal';
import { bookmarkService } from '../services/bookmarkService';
import { useDarkMode } from '../hooks/useDarkMode';

const Header = ({ signOut, isAdmin }) => {
  const location = useLocation();
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDark, setIsDark] = useDarkMode();

  const handleFeedbackSubmit = async (note) => {
    await bookmarkService.submitFeedback({ note, page: location.pathname });
  };

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const navLinkClass = "text-gray-600 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 font-medium transition-colors";
  const mobileNavLinkClass = "block px-4 py-3 text-gray-600 hover:text-primary-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:text-primary-400 dark:hover:bg-gray-700 font-medium transition-colors";

  return (
    <header className="bg-white dark:bg-gray-900 shadow-sm border-b border-gray-200 dark:border-gray-700">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center space-x-2">
            <BrandIcon className="h-8 w-8" />
            <span className="text-xl font-bold text-gray-900 dark:text-gray-100 brand-wordmark">BookMarker</span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex space-x-6">
            <Link to="/dashboard" className={navLinkClass}>Dashboard</Link>
            <Link to="/bookmarks" className={navLinkClass}>My Bookmarks</Link>
            <Link to="/collections" className={navLinkClass}>Collections</Link>
            <Link to="/tags" className={navLinkClass}>Tags</Link>
            <Link to="/visualize" className={navLinkClass}>Visualize</Link>
            <Link to="/analytics" className={navLinkClass}>Analytics</Link>
            {isAdmin && <Link to="/admin" className={navLinkClass}>Admin</Link>}
          </nav>

          {/* User Actions */}
          <div className="flex items-center space-x-4">
            <Link to="/add" className="btn-primary flex items-center space-x-2">
              <PlusIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Add Bookmark</span>
              <span className="sm:hidden">Add</span>
            </Link>

            <button
              onClick={() => setIsDark(!isDark)}
              className="hidden md:block p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-colors"
              aria-label="Toggle dark mode"
            >
              {isDark ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
            </button>

            <button
              onClick={() => setIsFeedbackOpen(true)}
              className="text-gray-400 hover:text-primary-600 dark:text-gray-500 dark:hover:text-primary-400 transition-colors"
              title="Send feedback"
            >
              <ChatBubbleLeftEllipsisIcon className="h-5 w-5" />
            </button>

            <Link to="/help" className="text-gray-400 hover:text-primary-600 dark:text-gray-500 dark:hover:text-primary-400 transition-colors" title="Help">
              <QuestionMarkCircleIcon className="h-5 w-5" />
            </Link>

            <Link to="/profile" className="text-gray-400 hover:text-primary-600 dark:text-gray-500 dark:hover:text-primary-400 transition-colors" title="Profile">
              <UserCircleIcon className="h-5 w-5" />
            </Link>

            <button
              onClick={signOut}
              className="hidden md:block text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 transition-colors"
            >
              Sign Out
            </button>

            {/* Hamburger — mobile only */}
            <button
              onClick={() => setIsMobileMenuOpen(o => !o)}
              className="md:hidden text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen
                ? <XMarkIcon className="h-6 w-6" />
                : <Bars3Icon className="h-6 w-6" />
              }
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu dropdown */}
      {isMobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <nav className="py-1">
            <Link to="/dashboard" className={mobileNavLinkClass} onClick={closeMobileMenu}>Dashboard</Link>
            <Link to="/bookmarks" className={mobileNavLinkClass} onClick={closeMobileMenu}>My Bookmarks</Link>
            <Link to="/collections" className={mobileNavLinkClass} onClick={closeMobileMenu}>Collections</Link>
            <Link to="/tags" className={mobileNavLinkClass} onClick={closeMobileMenu}>Tags</Link>
            <Link to="/visualize" className={mobileNavLinkClass} onClick={closeMobileMenu}>Visualize</Link>
            <Link to="/analytics" className={mobileNavLinkClass} onClick={closeMobileMenu}>Analytics</Link>
            {isAdmin && <Link to="/admin" className={mobileNavLinkClass} onClick={closeMobileMenu}>Admin</Link>}
            <Link to="/help" className={mobileNavLinkClass} onClick={closeMobileMenu}>Help</Link>
          </nav>
          <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-3 flex items-center justify-between">
            <button
              onClick={() => { signOut(); closeMobileMenu(); }}
              className="text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 transition-colors"
            >
              Sign Out
            </button>
            <button
              onClick={() => setIsDark(!isDark)}
              className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-colors"
              aria-label="Toggle dark mode"
            >
              {isDark ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
            </button>
          </div>
        </div>
      )}

      <FeedbackModal
        isOpen={isFeedbackOpen}
        onClose={() => setIsFeedbackOpen(false)}
        onSubmit={handleFeedbackSubmit}
      />
    </header>
  );
};

export default Header;
